import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import DataTable from '../components/DataTable/DataTable.jsx';
import Pagination from '../components/Pagination/Pagination.jsx';
import SpaceReservaManager from '../components/SpaceReservaManager/SpaceReservaManager.jsx';
import VerificadoCell from '../components/VerificadoCell/VerificadoCell.jsx';
import spacesApi from '../services/spacesApi.js';
import categoriesApi from '../services/categoriesApi.js';
import { colors } from '../utils/colors.js';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid ${colors.gray[100]};
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: ${(props) => (props.$active ? colors.primary[600] : colors.gray[500])};
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  font-size: 14px;
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;

  &:hover {
    color: ${colors.primary[600]};
  }
`;

const FilterSelect = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
  min-width: 220px;
`;

function flattenCategoryOptions(tree, depth = 0, acc = []) {
  for (const cat of tree) {
    acc.push({ id: cat.id, label: `${'— '.repeat(depth)}${cat.nome}` });
    if (cat.children?.length) flattenCategoryOptions(cat.children, depth + 1, acc);
  }
  return acc;
}

function SpacesList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [categorias, setCategorias] = useState([]);
  const [categoriaId, setCategoriaId] = useState('');

  useEffect(() => {
    categoriesApi
      .hierarchy()
      .then((data) => setCategorias(flattenCategoryOptions(data.categories || [])))
      .catch(() => setCategorias([]));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [categoriaId, perPage]);

  const loadSpaces = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (categoriaId) params.categoria_id = categoriaId;
    spacesApi
      .list(params)
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, categoriaId]);

  useEffect(() => {
    loadSpaces();
  }, [loadSpaces]);

  const columns = [
    { key: 'nome', label: 'Nome' },
    { key: 'slug', label: 'Slug' },
    { key: 'status', label: 'Status' },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (row) => new Date(row.created_at).toLocaleDateString('pt-BR'),
    },
    {
      key: 'verificado',
      label: 'Verificado',
      render: (row) => <VerificadoCell space={row} onSaved={loadSpaces} />,
    },
  ];

  return (
    <div>
      <Header>
        <FilterSelect value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
          <option value="">Todas as categorias</option>
          {categorias.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.label}
            </option>
          ))}
        </FilterSelect>
      </Header>
      <DataTable columns={columns} rows={items} loading={loading} />
      <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage} onPerPageChange={setPerPage} />
    </div>
  );
}

export default function Spaces() {
  const [activeTab, setActiveTab] = useState('spaces');

  return (
    <div>
      <Header>
        <Title>Spaces</Title>
      </Header>
      <TabsContainer>
        <Tab type="button" $active={activeTab === 'spaces'} onClick={() => setActiveTab('spaces')}>
          Spaces
        </Tab>
        <Tab type="button" $active={activeTab === 'reservados'} onClick={() => setActiveTab('reservados')}>
          Reservados
        </Tab>
      </TabsContainer>
      {activeTab === 'spaces' ? <SpacesList /> : <SpaceReservaManager />}
    </div>
  );
}
